/**
 * space.ts — 공간(space) 개념. B′ 안(2026-10-19 미리보기).
 *
 * 공간 = 사용자가 지금 일하는 "자리".
 *   my        : 내 업무 (할 일·결재·휴가·급여 — 법인을 가로지르는 개인 영역)
 *   joongang  : 중앙산업(레미콘) 업무
 *   gyeongwon : 경원산업(아스콘) 업무
 *
 * 주소(2026-10-20 1단계 D-01 → 10-22 D-26): 내 업무 = `/my…`, 법인 = `/c/{tenant}/{biz}/…`(사업 메뉴)·`/c/{tenant}/…`(법인 공통).
 *   공간의 정본은 URL 이다 — 새로고침·링크 공유 시 같은 법인이 열린다. 옛 주소(/sales 등)는 /c/joongang/… 로 리다이렉트.
 * 법인 소속(2026-10-23 D 묶음): 실제 소속 문서 `tenants/{t}/members/{uid}` 로 판정(membershipFrom). 가상 소속(MOCK_MEMBERSHIP)은
 * 개발 서버에서 `?mock=` 을 줄 때만 쓰는 미리보기 도구로 남겼다.
 *
 * 순수 타입·상수만. Firebase import 금지(domain 규칙).
 */

export type SpaceKey = "my" | "joongang" | "gyeongwon";
export type TenantKey = Exclude<SpaceKey, "my">;

/** 런타임 화이트리스트 — union 에 값을 추가하면 여기도 같이(localStorage·쿼리 파싱이 이걸로 검증). */
export const SPACE_KEYS: readonly SpaceKey[] = ["my", "joongang", "gyeongwon"] as const;
export const TENANT_KEYS: readonly TenantKey[] = ["joongang", "gyeongwon"] as const;

export function isSpaceKey(v: unknown): v is SpaceKey {
  return typeof v === "string" && (SPACE_KEYS as readonly string[]).includes(v);
}
export function isTenantKey(v: unknown): v is TenantKey {
  return typeof v === "string" && (TENANT_KEYS as readonly string[]).includes(v);
}

export interface SpaceMeta {
  /** 전체 이름 (PC 세그먼트·전환 시트) */
  label: string;
  /** 짧은 이름 (fold 세그먼트·MO 칩·배지) */
  short: string;
  /** 업종 부제 (전환 시트·전체 보기 칸 제목) */
  sub?: string;
  /** CSS 클래스 접미사 — `.is-space-{key}` 로 색 토큰(--space-c / --space-bg)을 고른다 */
  key: SpaceKey;
  /** 법인 색(본색·연한 배경) 토큰 — tokens.css `--c-tenant-*`. 내 업무는 중립 회색 */
  colorVar: string;
  tintVar: string;
}

export const SPACE_META: Record<SpaceKey, SpaceMeta> = {
  my: {
    key: "my", label: "내 업무", short: "내 업무",
    colorVar: "var(--c-text-strong)", tintVar: "var(--c-slate-200)",
  },
  joongang: {
    key: "joongang", label: "중앙산업", short: "중앙", sub: "레미콘",
    colorVar: "var(--c-tenant-joongang)", tintVar: "var(--c-tenant-joongang-2)",
  },
  gyeongwon: {
    key: "gyeongwon", label: "경원산업", short: "경원", sub: "아스콘",
    colorVar: "var(--c-tenant-gyeongwon)", tintVar: "var(--c-tenant-gyeongwon-2)",
  },
};

/** 법인 주소 접두어 — `/c/{tenant}/{menu}` */
export const TENANT_PATH_PREFIX = "/c";

/** 옛 주소(/sales 등 법인 구분 없는 경로)가 가리키던 법인. 지금 데이터는 전부 중앙 것이다. */
export const LEGACY_TENANT: TenantKey = "joongang";

/**
 * 데이터가 실제로 연결된 법인(미리보기). 경원은 ERP 자료 미연결 → 중앙 전용 실시간 구독(LIVE 마커·출하 알림 등)을
 * 경원 공간에서 돌리지 않는 판정에 쓴다. 경원 자료가 연결되면 여기에 추가하고, 구독도 tenant 경로로 바꾼다(2단계).
 */
export const DATA_CONNECTED_TENANTS: readonly TenantKey[] = ["joongang"] as const;
export function isTenantDataConnected(t: TenantKey | null | undefined): boolean {
  return !!t && DATA_CONNECTED_TENANTS.includes(t);
}

// ───────────────────────── 사업(biz) — 법인 › 사업 2단계 (D-26, 2026-10-22) ─────────────────────────
//
// 법인 = 회계·급여·직원 단위, 사업 = 판매·출하·자재·견적 단위. 중앙은 레미콘(+석산 추후), 경원은 아스콘.
// 주소: 사업 메뉴 `/c/{tenant}/{biz}/{menu}`, 법인 공통 메뉴(급여·원가·설정) `/c/{tenant}/{menu}`.
// 데이터: 사업별 `tenants/{법인}/units/{사업}/…`, 법인 공통 `tenants/{법인}/…` (domain/tenantPath.ts).

export type BizKey = "rmc" | "ascon" | "aggregate";

/** 런타임 화이트리스트 — union 에 값을 추가하면 여기도 같이. */
export const BIZ_KEYS: readonly BizKey[] = ["rmc", "ascon", "aggregate"] as const;
export function isBizKey(v: unknown): v is BizKey {
  return typeof v === "string" && (BIZ_KEYS as readonly string[]).includes(v);
}

/**
 * 사업 표시 이름. ready=false = 자리만 있는 사업(기본 사업 선택에서 제외).
 * gate = 그 사업 자체를 보는 권한(메뉴 게이트와 같은 `*.view` 키). 있으면 상단 사업 버튼·주소 진입(BizGate)을 이 권한으로 막는다.
 * 석산은 우선 "석산"으로 표시하고, 추후 공식 명칭 "골재"로 바꿀 예정(사용자 10-22) — 이 label 만 고치면 된다.
 * 석산(2026-10-29): 사업부지(등기부 지도) 메뉴 하나로 운영 시작 — `aggregate.view` 보유자만. 레미콘 메뉴는 붙이지 않는다(menus.ts `biz`).
 */
export const BIZ_META: Record<BizKey, { label: string; ready: boolean; gate?: string }> = {
  rmc: { label: "레미콘", ready: true },
  ascon: { label: "아스콘", ready: true },
  aggregate: { label: "석산", ready: true, gate: "aggregate.view" },
};

/** 사업 진입 권한 키(없으면 undefined = 법인 소속이면 누구나) */
export function bizGate(b: BizKey): string | undefined {
  return BIZ_META[b].gate;
}

/**
 * 법인별 사업(표시 순서). 중앙 = 레미콘 + 석산(별도 ERP 구축 전까지 준비중), 경원 = 아스콘.
 * 사업이 2개 이상인 법인은 상단 바 법인 칩 옆에 사업 버튼이 붙는다(SpaceSwitch).
 */
export const TENANT_BIZ: Record<TenantKey, readonly BizKey[]> = {
  joongang: ["rmc", "aggregate"],
  gyeongwon: ["ascon"],
};

export function isBizReady(b: BizKey): boolean {
  return BIZ_META[b].ready;
}

/** 기본 사업 = 그 법인의 첫 번째 운영 중(ready) 사업 */
export function defaultBiz(t: TenantKey): BizKey {
  return TENANT_BIZ[t].find(isBizReady) ?? TENANT_BIZ[t][0];
}
export function tenantHasBiz(t: TenantKey, b: unknown): b is BizKey {
  return isBizKey(b) && TENANT_BIZ[t].includes(b);
}

/**
 * 홈(대시보드) 보드 키 — 위젯 배치·설정·금일 출하 내용 열 설정을 사업 단위로 따로 저장할 때 쓴다(2026-10-23).
 * 예: `joongang_rmc`, `gyeongwon_ascon`. Firestore 문서 ID·localStorage 키 접미사로 쓰므로 `__` 로 시작하지 않는다.
 */
export function homeBoardKey(t: TenantKey, b: BizKey): string {
  return `${t}_${b}`;
}

/** 법인 공간 루트 — `/c/{tenant}` (라우터가 기본 사업 홈으로 다시 보낸다) */
export function tenantRootPath(t: TenantKey): string {
  return `${TENANT_PATH_PREFIX}/${t}`;
}

/** 사업 홈(대시보드) — `/c/{tenant}/{biz}` */
export function bizHomePath(t: TenantKey, b: BizKey = defaultBiz(t)): string {
  return `${tenantRootPath(t)}/${b}`;
}

/** 공간별 홈 경로. 내 업무 = /my, 법인 = 기본 사업 홈 /c/{tenant}/{biz}. */
export function spaceHomePath(space: SpaceKey): string {
  return space === "my" ? "/my" : bizHomePath(space);
}

/** 경로 → 그 경로가 가리키는 공간. 법인 경로면 주소의 tenant 값(검증 전 원문)과 사업도 돌려준다. */
export interface ParsedSpacePath {
  /** "my" | 유효한 tenant | null(법인 구분 없는 옛 주소·기타) */
  space: SpaceKey | null;
  /** /c/{x} 의 x 원문 — 화이트리스트 밖 값 판별용 */
  tenantRaw: string | null;
  /** /c/{t}/{biz} 의 사업(그 법인에 있는 사업일 때만). 법인 공통 메뉴·법인 루트면 null */
  biz: BizKey | null;
  /** 공간(+사업) 접두어를 뗀 나머지 경로 ("/sales", 홈이면 "/") */
  rest: string;
}
export function parseSpacePath(pathname: string): ParsedSpacePath {
  const segs = pathname.split("/").filter(Boolean);
  if (segs[0] === "my") return { space: "my", tenantRaw: null, biz: null, rest: "/" + segs.slice(1).join("/") };
  if (`/${segs[0]}` === TENANT_PATH_PREFIX && segs.length >= 2) {
    const raw = segs[1];
    const space = isTenantKey(raw) ? raw : null;
    if (space && tenantHasBiz(space, segs[2])) {
      return { space, tenantRaw: raw, biz: segs[2] as BizKey, rest: "/" + segs.slice(3).join("/") };
    }
    return { space, tenantRaw: raw, biz: null, rest: "/" + segs.slice(2).join("/") };
  }
  return { space: null, tenantRaw: null, biz: null, rest: "/" + segs.join("/") };
}

/**
 * 같은 법인 안에서 사업만 바꾼 주소 — 사업 메뉴를 보고 있으면 같은 메뉴(…/rmc/sales → …/aggregate/sales),
 * 법인 공통 메뉴·법인 밖이면 그 사업 홈.
 */
export function swapBizInPath(pathname: string, t: TenantKey, b: BizKey): string {
  const p = parseSpacePath(pathname);
  if (p.space === t && p.biz !== null && p.rest !== "/") return `${bizHomePath(t, b)}${p.rest}`;
  return bizHomePath(t, b);
}

/**
 * 같은 화면을 다른 법인 주소로 — /c/joongang/rmc/sales → /c/gyeongwon/ascon/sales,
 * 법인 공통 /c/joongang/payroll → /c/gyeongwon/payroll. 법인 경로가 아니면 그 법인 홈.
 * 사업은 대상 법인에 같은 사업이 있으면 유지, 없으면 대상 법인의 기본 사업.
 */
export function swapTenantInPath(pathname: string, next: TenantKey): string {
  const p = parseSpacePath(pathname);
  if (p.tenantRaw === null) return spaceHomePath(next);
  if (p.biz === null) return p.rest === "/" ? spaceHomePath(next) : `${tenantRootPath(next)}${p.rest}`;
  const b = tenantHasBiz(next, p.biz) ? p.biz : defaultBiz(next);
  return p.rest === "/" ? bizHomePath(next, b) : `${bizHomePath(next, b)}${p.rest}`;
}

// ───────────────────────── 소속 (실제 + 미리보기) ─────────────────────────

/**
 * 공간 판정에 쓰는 소속 요약 — 실제 소속 문서(membershipFrom) 또는 미리보기 가상 소속(MOCK_MEMBERSHIP).
 * source: real = 소속 문서 · legacy = 소속 문서가 하나도 없는 옛 계정(중앙만, 운영 무중단) · mock = 개발 서버 미리보기
 */
export interface SpaceMembership {
  source: "real" | "legacy" | "mock";
  /** 설명 라벨 (미리보기 표시용) */
  label: string;
  /** 업무 공간이 열리는 법인. 비어 있으면 내 업무만. */
  tenants: TenantKey[];
  /**
   * 근무 형태가 직원·지입인 근무 법인(소속 문서 employment 파생) — 내 업무 목업 전용.
   * 급여·연차의 정본은 급여 법인(`employees.companies`, 급여 명부)이며 근무 법인과 다를 수 있다.
   * 내 업무 급여 위젯을 실데이터로 바꿀 때 급여 명부 기준으로 교체할 것(설계 12 §16-1).
   */
  employers: TenantKey[];
  /** 법인 관리자로 등록된 법인(D-08). 전체 관리자는 두 법인 모두. */
  adminOf: TenantKey[];
  /** 전체(그룹) 관리자 — 실제 소속은 claim is_admin 그대로 */
  groupAdmin: boolean;
}

/** membershipFrom 입력 — 소속 문서의 필요한 필드만(domain/membership.ts MemberRow 와 호환) */
export interface MembershipRowLite {
  tenant: TenantKey;
  active: boolean;
  tenantAdmin: boolean;
  employed: boolean;
}

/**
 * 실제 소속 문서 → 공간 소속 요약(12 §8-C·§16).
 *   - 전체 admin: 두 법인 공간 + 두 법인 관리. 고용 법인은 소속 문서대로.
 *   - 소속 문서가 하나도 없는 옛 계정(운영 users 6개 중 비admin — 삭제 후 재생성 예정, 12 §0): 중앙만(지금 동작 유지).
 *     종료된 소속 문서라도 하나 있으면 이 대체를 쓰지 않는다(퇴사자에게 중앙이 다시 열리지 않게).
 *   - 그 밖: 활성 근무 법인만.
 */
export function membershipFrom(rows: readonly MembershipRowLite[], isAdmin: boolean, anyDoc: boolean): SpaceMembership {
  const active = rows.filter((r) => r.active);
  const employers = TENANT_KEYS.filter((t) => active.some((r) => r.tenant === t && r.employed));
  if (isAdmin) {
    return { source: "real", label: "전체 관리자", tenants: [...TENANT_KEYS], employers, adminOf: [...TENANT_KEYS], groupAdmin: true };
  }
  if (!anyDoc) {
    return { source: "legacy", label: "소속 문서 없음(중앙)", tenants: [LEGACY_TENANT], employers: [LEGACY_TENANT], adminOf: [], groupAdmin: false };
  }
  return {
    source: "real",
    label: "소속",
    tenants: TENANT_KEYS.filter((t) => active.some((r) => r.tenant === t)),
    employers,
    adminOf: TENANT_KEYS.filter((t) => active.some((r) => r.tenant === t && r.tenantAdmin)),
    groupAdmin: false,
  };
}

/** 소속 확인 전(전체 admin 아님) — 공간을 열지 않는다 */
export const PENDING_MEMBERSHIP: SpaceMembership = {
  source: "real", label: "확인 중", tenants: [], employers: [], adminOf: [], groupAdmin: false,
};

/** `?mock=both|jadmin|kadmin|joongang|driver` — 겸직(전체 관리자) / 중앙 관리자 / 경원 관리자 / 한 법인 / 기사. `?mock=off` = 실제 소속 */
export type MockMode = "both" | "jadmin" | "kadmin" | "joongang" | "driver";
export const MOCK_MODES: readonly MockMode[] = ["both", "jadmin", "kadmin", "joongang", "driver"] as const;
export function isMockMode(v: unknown): v is MockMode {
  return typeof v === "string" && (MOCK_MODES as readonly string[]).includes(v);
}
/** 가상 소속을 끄고 실제 소속으로 돌아가는 쿼리 값 */
export const MOCK_OFF = "off";

export interface MockMembership extends SpaceMembership {
  mode: MockMode;
}

export const MOCK_MEMBERSHIP: Record<MockMode, MockMembership> = {
  both: { source: "mock", mode: "both", label: "겸직·전체 관리자", tenants: ["joongang", "gyeongwon"], employers: ["joongang", "gyeongwon"], adminOf: [], groupAdmin: true },
  jadmin: { source: "mock", mode: "jadmin", label: "중앙 관리자", tenants: ["joongang"], employers: ["joongang"], adminOf: ["joongang"], groupAdmin: false },
  kadmin: { source: "mock", mode: "kadmin", label: "경원 관리자", tenants: ["gyeongwon"], employers: ["gyeongwon"], adminOf: ["gyeongwon"], groupAdmin: false },
  joongang: { source: "mock", mode: "joongang", label: "한 법인(중앙)", tenants: ["joongang"], employers: ["joongang"], adminOf: [], groupAdmin: false },
  driver: { source: "mock", mode: "driver", label: "기사", tenants: [], employers: ["joongang"], adminOf: [], groupAdmin: false },
};

/**
 * 관리 칸을 볼 범위. 전체 관리자 = 두 법인 + 그룹 항목(가입 승인), 법인 관리자 = 그 법인 항목만.
 * 미리보기(mock): 실제 isAdmin 이 있어도 가상 소속이 전체 관리자가 아니면 법인 관리자/일반으로 본다.
 */
export interface AdminScope {
  group: boolean;
  tenants: TenantKey[];
}
export function adminScopeOf(m: SpaceMembership, isAdmin: boolean): AdminScope {
  if (isAdmin && m.groupAdmin) return { group: true, tenants: ["joongang", "gyeongwon"] };
  return { group: false, tenants: m.adminOf };
}

/** 미리보기 도구(가상 소속·사이드바 색조 바꾸기)는 개발 서버에서만 — 운영 빌드는 늘 실제 소속 */
export const PREVIEW_TOOLS = import.meta.env.DEV;

/** 소속 → 열 수 있는 공간 목록 (항상 내 업무가 먼저) */
export function spacesFor(m: SpaceMembership): SpaceKey[] {
  return ["my", ...m.tenants];
}
